"use client";
import { ChevronLeft, ChevronRight, Check } from "lucide-react";
import Image from "next/image";
import { Metadata } from "next";

export const metadata: Metadata = {
  title: "Order Kisan Mithraa Starters",
};
declare global {
  interface Window {
    Razorpay: new (options: unknown) => {
      open: () => void;
      on: (event: string, callback: () => void) => void;
    };
  }
}
import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";

export default function Order() {
  const remoteImages = [
    "/remoteStarterOrder.png",
    "/REMOTE1.png",
    "/remote2.png",
  ];
  const mobileImages = ["/1.png", "/2.png", "/mobile5.png"];

  const [selectedProduct, setSelectedProduct] =
    useState<keyof typeof products>("mobile");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);

  const products = {
    remote: {
      name: "Kisan Mithraa Remote Starter",
      price: 2199,
      images: remoteImages,
      features: [
        "Remote control up to 2KM range",
        "Easy installation",
        "Battery backup included",
        "12 months warranty",
        "Inbuilt Automatic Starter",
      ],
    },
    mobile: {
      name: "Kisan Mithraa Mobile Starter",
      price: 5999,
      images: mobileImages,
      features: [
        "Remote motor control via mobile/SMS",
        "Dry run protection with auto-restart",
        "Timer modes: Clock, cyclic, multi-slot",
        "Voltage, overload, and drain settings",
        "Real-time alerts for ON/OFF and faults",
        "Multi-language support",
        "Battery backup for power outages",
        "1-year replacement guarantee",
      ],
    },
  };

  const currentProduct = products[selectedProduct];
  const totalAmount = quantity * currentProduct.price;

  const [customer, setCustomer] = useState({
    name: "",
    email: "",
    contact: "",
    address: "",
    pincode: "",
  });

  const handleNext = useCallback(
    () => setCurrentImageIndex((i) => (i + 1) % currentProduct.images.length),
    [currentProduct.images.length],
  );

  const handlePrev = useCallback(
    () =>
      setCurrentImageIndex((i) =>
        i === 0 ? currentProduct.images.length - 1 : i - 1,
      ),
    [currentProduct.images.length],
  );

  useEffect(() => {
    setCurrentImageIndex(0);
  }, [selectedProduct]);

  useEffect(() => {
    const interval = setInterval(handleNext, 8000);
    return () => clearInterval(interval);
  }, [handleNext]);

  // Load Razorpay script
  const loadRazorpayScript = (): Promise<boolean> => {
    return new Promise((resolve) => {
      // if script already present, resolve true
      const existing = document.querySelector(
        `script[src="https://checkout.razorpay.com/v1/checkout.js"]`,
      );
      if (existing) return resolve(true);

      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    // Validate form
    if (
      !customer.name ||
      !customer.email ||
      !customer.contact ||
      !customer.address
    ) {
      alert("Please fill in all fields");
      return;
    }

    if (customer.contact.length !== 10) {
      alert("Please enter a valid 10-digit contact number");
      return;
    }

    setIsProcessing(true);

    // Load Razorpay script
    const scriptLoaded = await loadRazorpayScript();
    if (!scriptLoaded) {
      alert("Failed to load payment gateway. Please try again.");
      setIsProcessing(false);
      return;
    }

    try {
      // Step 1: Create order on your backend
      const orderResponse = await fetch("/api/createOrder", {
        // <- match your API route folder name exactly
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: totalAmount,
          productName: currentProduct.name,
          quantity: quantity,
          customer: customer,
        }),
      });

      const orderData = await orderResponse.json();

      if (!orderData.orderId) {
        throw new Error("Failed to create order");
      }

      // Step 2: Configure Razorpay options
      const options = {
        key: "rzp_live_RWp3zATSieLNhY", // Replace with your Razorpay Key ID
        amount: totalAmount * 100, // Amount in paise
        currency: "INR",
        name: "PARA INNOVATION ",
        description: `${currentProduct.name} - Qty: ${quantity}`,
        image: "/Para_logo.webp", // Your company logo
        order_id: orderData.orderId,
        handler: async function (response: {
          razorpay_order_id: string;
          razorpay_payment_id: string;
          razorpay_signature: string;
        }) {
          // Step 3: Verify payment on backend
          try {
            const verifyResponse = await fetch("/api/verify-payment", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                customer: customer,
                product: currentProduct.name,
                quantity: quantity,
                amount: totalAmount,
              }),
            });

            const verifyData = await verifyResponse.json();

            if (verifyData.success) {
              await fetch(`/api/send-sms`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  to: customer.contact,
                  message: `Dear ${customer.name}, your order for ${quantity} x ${currentProduct.name} has been successfully placed. Order ID: ${response.razorpay_order_id}. Thank you for ordering with Kisan Mithraa!`,
                }),
              });
              await fetch("/api/send-order-email", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  orderId: response.razorpay_order_id,
                  product: currentProduct.name,
                  quantity,
                  amount: totalAmount,
                  customer,
                }),
              });
              window.location.href = `/checkoutSuccess?orderId=${response.razorpay_order_id}`;
              alert("✅ Payment successful! Redirecting to confirmation page.");
              // Optionally, send SMS here via /api/send-sms
            } else {
              alert("❌ Payment verification failed. Please contact support.");
            }
          } catch (error) {
            alert(
              "Error verifying payment. Please contact support +91 9032747058.",
            );
            console.error(error);
          }
        },
        prefill: {
          name: customer.name,
          email: customer.email,
          contact: customer.contact,
        },
        notes: {
          address: customer.address,
          product: currentProduct.name,
        },
        theme: {
          color: "#8ee1acff",
        },
        modal: {
          ondismiss: function () {
            setIsProcessing(false);
          },
        },
      };

      // Step 4: Open Razorpay checkout
      const razorpay = new window.Razorpay(options);
      razorpay.open();
      setIsProcessing(false);
    } catch (error) {
      alert("Error processing payment. Please try again.");
      console.error(error);
      setIsProcessing(false);
    }
  };

  return (
    <section className="relative w-full bg-gradient-to-b from-gray-50 to-white text-gray-900 py-16 px-4 lg:px-20">
      <div className="max-w-7xl mx-auto">
        <motion.h1
          className="text-4xl md:text-5xl font-bold text-center mb-12 text-gray-900 hover:text-[#6BAF92]"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          Choose Your Starter
        </motion.h1>

        {/* Product Selection Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <motion.div
            onClick={() => setSelectedProduct("remote")}
            className={`relative p-6 rounded-2xl cursor-pointer transition-all duration-300 ${
              selectedProduct === "remote"
                ? "bg-gradient-to-br from-green-50 to-green-100 border-3 border-green-500 shadow-2xl"
                : "bg-white border-2 border-gray-200 hover:border-green-300 shadow-lg"
            }`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {selectedProduct === "remote" && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-3 -right-3 bg-green-500 text-white rounded-full p-2 shadow-lg"
              >
                <Check size={24} />
              </motion.div>
            )}
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Remote Starter
                </h3>
                <p className="text-3xl font-bold text-green-600">₹2,199</p>
              </div>
            </div>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span> 2KM Range
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span> Physical Remote
                Control
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span> Easy to Use
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span> 12 months Warranty
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span> Inbuilt Automatic
                Starter
              </li>
            </ul>
          </motion.div>

          <motion.div
            onClick={() => setSelectedProduct("mobile")}
            className={`relative p-6 rounded-2xl cursor-pointer transition-all duration-300 ${
              selectedProduct === "mobile"
                ? "bg-gradient-to-br from-blue-50 to-blue-100 border-3 border-green-500 shadow-2xl"
                : "bg-white border-2 border-gray-200 hover:border-green-300 shadow-lg"
            }`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {selectedProduct === "mobile" && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-3 -right-3 bg-green-500 text-white rounded-full p-2 shadow-lg"
              >
                <Check size={24} />
              </motion.div>
            )}
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Mobile Starter
                </h3>
                <p className="text-3xl font-bold text-green-600">₹5,999</p>
              </div>
            </div>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-center gap-2">
                <span className="text-blue-500">✓</span> Unlimited Range
              </li>
              <li className="flex items-center gap-2">
                <span className="text-blue-500">✓</span> Mobile & SMS Control
              </li>
              <li className="flex items-center gap-2">
                <span className="text-blue-500">✓</span> Advanced Features
              </li>
              <li className="flex items-center gap-2">
                <span className="text-blue-500">✓</span> 1-Year Warranty &
                Guarentee
              </li>
            </ul>
          </motion.div>
        </div>

        {/* Product Details Section */}
        <motion.div
          key={selectedProduct}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col lg:flex-row gap-10"
        >
          {/* Image Slider */}
          <div className="lg:w-1/2 flex flex-col items-center">
            <div className="relative w-full max-w-md md:max-w-lg lg:max-w-xl my-auto overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentImageIndex}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.5 }}
                  className="relative w-full aspect-square"
                >
                  <Image
                    src={currentProduct.images[currentImageIndex]}
                    alt={`${currentProduct.name} - Image ${currentImageIndex + 1}`}
                    fill
                    className="object-contain"
                    priority={currentImageIndex === 0}
                  />
                </motion.div>
              </AnimatePresence>

              <motion.button
                onClick={handlePrev}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white text-gray-900 rounded-full p-3 shadow-lg hover:bg-gray-100"
              >
                <ChevronLeft size={24} />
              </motion.button>
              <motion.button
                onClick={handleNext}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white text-gray-900 rounded-full p-3 shadow-lg hover:bg-gray-100"
              >
                <ChevronRight size={24} />
              </motion.button>
            </div>

            {/* Thumbnails */}
            <div className="flex gap-3 mt-6">
              {currentProduct.images.map((img, idx) => (
                <motion.div
                  key={idx}
                  onClick={() => setCurrentImageIndex(idx)}
                  className={`relative w-20 h-20 rounded-lg overflow-hidden cursor-pointer bg-gray-100 flex items-center justify-center ${
                    currentImageIndex === idx
                      ? "ring-4 ring-green-500"
                      : "ring-2 ring-gray-200"
                  }`}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="text-xs text-gray-400">{idx + 1}</span>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Product Info */}
          <div className="lg:w-1/2 flex flex-col gap-6">
            <motion.h2
              className="text-4xl font-bold text-gray-900"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              {currentProduct.name}
            </motion.h2>

            <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-xl">
              <p className="text-3xl font-bold text-gray-900">
                ₹{currentProduct.price.toLocaleString()}
                <span className="text-sm font-normal text-gray-600 ml-2">
                  (INCL. OF ALL TAXES)
                </span>
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">
                Features:
              </h3>
              <ul className="space-y-2">
                {currentProduct.features.map((feature, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="flex items-start gap-2 text-gray-700"
                  >
                    <span className="text-green-500 mt-1">•</span>
                    <span>{feature}</span>
                  </motion.li>
                ))}
              </ul>
            </div>

            <motion.button
              onClick={() => setShowForm(!showForm)}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="px-8 py-4 bg-[#6BAF92] hover:bg-[#5EA785] text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all text-lg cursor-pointer"
            >
              {showForm ? "Close Form" : "Buy Now"}
            </motion.button>

            {/* Order Form */}
            {showForm && (
              <motion.div
                className="flex flex-col gap-4 p-6 bg-gray-50 rounded-2xl shadow-inner"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
              >
                <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                  Complete Your Order
                </h3>

                <div className="flex flex-col gap-4">
                  <motion.input
                    type="text"
                    placeholder="Full Name"
                    value={customer.name}
                    onChange={(e) =>
                      setCustomer({ ...customer, name: e.target.value })
                    }
                    required
                    className="border-2 border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                  />
                  <motion.input
                    type="email"
                    placeholder="Email"
                    value={customer.email}
                    onChange={(e) =>
                      setCustomer({ ...customer, email: e.target.value })
                    }
                    required
                    className="border-2 border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                  />
                  <motion.input
                    type="tel"
                    placeholder="Contact (10 digits) *"
                    value={customer.contact}
                    onChange={(e) =>
                      setCustomer({
                        ...customer,
                        contact: e.target.value.replace(/\D/g, "").slice(0, 10),
                      })
                    }
                    required
                    className="border-2 border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  />
                  <motion.textarea
                    placeholder="Delivery Address"
                    value={customer.address}
                    onChange={(e) =>
                      setCustomer({ ...customer, address: e.target.value })
                    }
                    required
                    rows={3}
                    className="border-2 border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  />
                  {/* Pin Code */}
                  <motion.input
                    type="text"
                    placeholder="Pin Code (Optional)"
                    value={customer.pincode || ""}
                    onChange={(e) =>
                      // keep only digits, max 6 characters (India pin)
                      setCustomer({
                        ...customer,
                        pincode: e.target.value.replace(/\D/g, "").slice(0, 6),
                      })
                    }
                    required
                    className="border-2 border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                  />
                </div>

                <div className="flex items-center gap-4 bg-white p-4 rounded-lg">
                  <span className="font-semibold text-gray-700">Quantity:</span>
                  <motion.button
                    type="button"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    whileTap={{ scale: 0.9 }}
                    className="w-10 h-10 bg-gray-200 rounded-full font-bold text-xl hover:bg-gray-300"
                  >
                    −
                  </motion.button>
                  <span className="text-2xl font-bold w-12 text-center">
                    {quantity}
                  </span>
                  <motion.button
                    type="button"
                    onClick={() => setQuantity(quantity + 1)}
                    whileTap={{ scale: 0.9 }}
                    className="w-10 h-10 bg-gray-200 rounded-full font-bold text-xl hover:bg-gray-300"
                  >
                    +
                  </motion.button>
                </div>

                <div className="bg-gradient-to-r from-green-100 to-blue-100 p-4 rounded-lg">
                  <p className="text-2xl font-bold text-gray-900">
                    Total Amount: ₹{totalAmount.toLocaleString()}
                  </p>
                </div>

                <motion.button
                  onClick={handlePayment}
                  disabled={isProcessing}
                  whileHover={{ scale: isProcessing ? 1 : 1.03 }}
                  whileTap={{ scale: isProcessing ? 1 : 0.97 }}
                  className={`px-8 py-4 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all text-lg ${
                    isProcessing
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-gradient-to-r from-blue-500 to-blue-600 text-white cursor-pointer"
                  }`}
                >
                  {isProcessing ? "Processing..." : "Proceed to Payment"}
                </motion.button>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
